// framework/client/core/LocalDataProvider.js
import { DataProvider } from "./DataProvider.js";

export class LocalDataProvider extends DataProvider {
    constructor(config) {
        super(config);
        this.data = config.data || []; // Initialize with provided data, or an empty array
        this.totalCount = this.data.length;
        this.idField = config.idField || '_id'; // Use _id as default idField
    }

    async initialize() {
        // No async initialization needed for local data
        this.log.info(`LocalDataProvider initialized with ${this.data.length} items.`);
    }

    async loadData(params = {}) {
        const { page = 1, pageSize = this.config.pageSize, filter = {}, sort = {} } = params;
        let filteredData = [...this.data]; // Work on a copy

        // --- Filtering ---
        if (Object.keys(filter).length > 0) {
            filteredData = filteredData.filter(item => {
                return Object.entries(filter).every(([field, value]) => {
                    // Basic filtering (exact match, case-insensitive for strings)
                    if (typeof value === 'string') {
                        return (item[field] || '').toLowerCase().includes(value.toLowerCase());
                    } else {
                        return item[field] === value;
                    }
                });
            });
        }

        // --- Sorting ---
        const sortField = Object.keys(sort)[0];
        const sortOrder = sort[sortField]; // Should be 'asc' or 'desc'

        if (sortField && sortOrder) {
            filteredData.sort((a, b) => {
                const aValue = a[sortField];
                const bValue = b[sortField];

                if (aValue < bValue) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        // --- Pagination ---
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        this.totalCount = filteredData.length; // Update totalCount after filtering
        this.emit(`dataLoaded`, {
            data: paginatedData,
            totalCount: this.totalCount
        });

        return { data: paginatedData, totalCount: this.totalCount };
    }

    async insert(item) {
        // Generate a unique ID if not provided
        if (!item[this.idField]) {
            item[this.idField] = this.generateId();
        }
        this.data.push(item);
        this.totalCount++;
        this.emit(`dataInserted`, item);
        return item;
    }

    async update(item) {
        const id = item[this.idField];
        const index = this.data.findIndex(i => i[this.idField] === id);
        if (index === -1) {
            throw new Error(`Item with id ${id} not found`);
        }
        this.data[index] = { ...this.data[index], ...item };
        this.emit(`dataUpdated`, this.data[index]);
        return this.data[index];
    }

    async delete(item) {
        const id = item[this.idField] || item; // Allow deleting by ID or by the item itself
        const index = this.data.findIndex(i => i[this.idField] === id);
        if (index === -1) {
            throw new Error(`Item with id ${id} not found`);
        }
        this.data.splice(index, 1);
        this.totalCount--;
        this.emit(`dataDeleted`);
        return true; // Indicate successful deletion
    }

    generateId() {
        // Basic ID generation (replace with a more robust solution if needed)
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }

    // Add other methods as needed (e.g., findById, clearData)
    async findById(id) {
        return this.data.find(item => item[this.idField] === id);
    }

    // --- Added Methods ---
    setData(data, totalCount = null) {
        this.data = data;
        this.totalCount = totalCount !== null ? totalCount : this.data.length;
        this.emit(`dataChanged`, { data: this.data, totalCount: this.totalCount });
    }

    getData() {
        return this.data;
    }

    clearData() {
        this.data = [];
        this.totalCount = 0;
        this.emit(`dataChanged`, { data: this.data, totalCount: this.totalCount });
    }

    // Override to avoid reference resolution
    async resolveReferences(data) {
        return data;
    }

    // Override since we don't need options from server
    async getReferenceFieldOptions(fieldName) {
        return [];
    }
}